import { PayloadSample } from './PayloadExecutor';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const j = (o: object) => JSON.stringify(o, null, 2);


// ─── Shared base objects ──────────────────────────────────────────────────────

const baseShipment32 = (id: string, legs: object[]) => ({
    version: '3.2',
    // responseFormat: 'standard', // removed option marker
    shipments: [{
        id,
        weight: 1000,
        // Optional shipment fields:
        // customerReference: 'CUST-REF-001',
        // reference: 'ORD-2024-001',
        // dateOfShipment: '2024-06-15T08:00:00',
        // commodity: 'Electronics',
        // handling: false,
        // volume: 2.5,
        // volumeUnit: 'M3', // M3 or CM3
        // weightUnit: 'kg', // only kg is supported
        // quote: false,
        // cooling: false,
        // subCompany: 'SubCorp',
        // branch: 'Branch-A',
        legs
    }],
});

const baseShipment30 = (id: string, legs: object[]) => ({
    version: '3.0',
    // responseFormat: 'standard', // removed option marker
    shipments: [{
        id,
        weight: 1000,
        // Optional shipment fields:
        // customerReference: 'CUST-REF-001',
        // reference: 'ORD-2024-001',
        // dateOfShipment: '2024-06-15T08:00:00',
        // commodity: 'Electronics',
        // handling: false,
        // volume: 2.5,
        // volumeUnit: 'M3', // M3 or CM3
        // weightUnit: 'kg', // only kg is supported
        // quote: false,
        // cooling: false,
        // subCompany: 'SubCorp',
        // branch: 'Branch-A',
        legs
    }],
});

const baseShipment28 = (id: string, legs: object[]) => ({
    version: '2.8',
    // responseFormat: 'standard', // removed option marker
    shipments: [{
        id,
        weight: 1000,
        // Optional shipment fields:
        // customerReference: 'CUST-REF-001',
        // reference: 'ORD-2024-001',
        // dateOfShipment: '2024-06-15T08:00:00',
        // commodity: 'Electronics',
        // handling: false,
        // volume: 2.5,
        // volumeUnit: 'M3', // M3 or CM3
        // weightUnit: 'kg', // only kg is supported
        // quote: false,
        // cooling: false,
        legs
    }],
});

// ─── XML helpers ──────────────────────────────────────────────────────────────

const xmlWrap = (ver: string, shipmentId: string, apiNote: string, legXml: string) =>
    `<CarbonCareApi Version="${ver}">
  <Request>
    <ApiKey>YOUR_API_KEY</ApiKey>
    <Shipments>
      <Shipment Id="${shipmentId}">
        <Weight>1000</Weight>${apiNote}
        <Legs>
          <Leg>
${legXml}
          </Leg>
        </Legs>
      </Shipment>
    </Shipments>
  </Request>
</CarbonCareApi>`;

const xmlWrap30 = (shipmentId: string, apiNote: string, legXml: string) =>
    xmlWrap('3.0', shipmentId, apiNote, legXml).replace('<CarbonCareApi', '<CarbonCareRequestApi').replace('</CarbonCareApi>', '</CarbonCareRequestApi>');

// ─── v3.2 Samples ─────────────────────────────────────────────────────────────

const AIR_32: PayloadSample = {
    json: `{
  "version": "3.2",
  "shipments": [
    {
      "id": "air-sample",
      "weight": 1000,
      // "customerReference": "CUST-REF-001",
      // "reference": "ORD-2024-001",
      // "dateOfShipment": "2024-06-15T08:00:00",
      // "commodity": "Electronics",
      // "handling": false,
      // "volume": 2.5,
      // "volumeUnit": "M3",
      // "quote": false,
      // "cooling": false,
      "legs": [
        {
          "transportMode": "Air",
          "air": {
            "from": {
              "airportCode": "FRA",
              // "coordinates": {"lat": 50.0379, "long": 8.5622}
            },
            "to": {
              "airportCode": "JFK",
              // "coordinates": {"lat": 40.6413, "long": -73.7781}
            },
            "isFreighter": false,
            "airType": "ABA",
          }
        }
      ]
    }
  ]
}`,
    xml: xmlWrap('3.2', 'air-sample', `
        <!-- Optional fields:
        <CustomerReference>CUST-REF-001</CustomerReference>
        <Reference>ORD-2024-001</Reference>
        <DateOfShipment>2024-06-15T08:00:00</DateOfShipment>
        <Commodity>Electronics</Commodity>
        <Handling>false</Handling>
        <Volume>2.5</Volume>
        <VolumeUnit>M3</VolumeUnit>
        <Quote>false</Quote>
        <Cooling>false</Cooling>
        -->`, `            <Air>
              <From><AirPortCode>FRA</AirPortCode></From>
              <To><AirPortCode>JFK</AirPortCode></To>
              <IsFreighter>false</IsFreighter>
              <AirType>ABA</AirType>
            </Air>`),
};

const SEA_32: PayloadSample = {
    json: `{
  "version": "3.2",
  // "responseFormat": "standard",
  "shipments": [
    {
      "id": "sea-sample",
      "weight": 1000,
      // "customerReference": "CUST-REF-001",
      // "reference": "ORD-2024-001",
      // "dateOfShipment": "2024-06-15T08:00:00",
      // "commodity": "Containerized Goods",
      // "handling": false,
      // "volume": 10.5,
      // "volumeUnit": "M3",
      // "quote": false,
      // "cooling": false,
      "legs": [
        {
          "transportMode": "Sea",
          "sea": {
            "from": {
              "seaportCode": "DEHAM",
              // "coordinates": {"lat": 53.5400, "long": 9.9900}
            },
            "to": {
              "seaportCode": "NLRTM",
              // "coordinates": {"lat": 51.9225, "long": 4.2281}
            }
          }
        }
      ]
    }
  ]
}`,
    xml: xmlWrap('3.2', 'sea-sample', `
        <!-- Optional fields:
        <CustomerReference>CUST-REF-001</CustomerReference>
        <Reference>ORD-2024-001</Reference>
        <DateOfShipment>2024-06-15T08:00:00</DateOfShipment>
        <Commodity>Containerized Goods</Commodity>
        <Handling>false</Handling>
        <Volume>10.5</Volume>
        <VolumeUnit>M3</VolumeUnit>
        <Quote>false</Quote>
        <Cooling>false</Cooling>
        -->`, `            <Sea>
              <From><SeaPortCode>DEHAM</SeaPortCode></From>
              <To><SeaPortCode>NLRTM</SeaPortCode></To>
            </Sea>`),
};

const RAIL_32: PayloadSample = {
    json: `{
  "version": "3.2",
  // "responseFormat": "standard", // removed option marker
  "shipments": [
    {
      "id": "rail-sample",
      "weight": 1000,
      // "customerReference": "CUST-REF-001", // removed option marker
      // "reference": "ORD-2024-001", // removed option marker
      // "dateOfShipment": "2024-06-15T08:00:00", // removed option marker
      // "commodity": "General Cargo", // removed option marker
      // "handling": false, // removed option marker
      // "volume": 5.0, // removed option marker
      // "volumeUnit": "M3", // removed option marker
      // "quote": false, // removed option marker
      // "cooling": false, // removed option marker
      "legs": [
        {
          "transportMode": "Rail",
          "rail": {
            "from": {
              "postalCode": "10115",
              "twoLetterCountry": "DE",
              // "city": "Berlin", // removed option marker
              // "street": "Station St 1"
            },
            "to": {
              "postalCode": "75001",
              "twoLetterCountry": "FR",
              // "city": "Paris", // removed option marker
              // "street": "Station St 100"
            },
            "type": "RAIL",
            // "truckType": "R18D"
          }
        }
      ]
    }
  ]
}`,
    xml: xmlWrap('3.2', 'rail-sample', `
        <!-- Optional fields:
        <CustomerReference>CUST-REF-001</CustomerReference>
        <Reference>ORD-2024-001</Reference>
        <DateOfShipment>2024-06-15T08:00:00</DateOfShipment>
        <Commodity>General Cargo</Commodity>
        <Handling>false</Handling>
        <Volume>5.0</Volume>
        <VolumeUnit>M3</VolumeUnit>
        <Quote>false</Quote>
        <Cooling>false</Cooling>
        -->`, `            <Rail>
              <From><Address><PostalCode>10115</PostalCode><TwoLetterCountry>DE</TwoLetterCountry></Address></From>
              <To><Address><PostalCode>75001</PostalCode><TwoLetterCountry>FR</TwoLetterCountry></Address></To>
              <Type>RAIL</Type>
              <!-- Optional: <TruckType>R18D</TruckType> (only for RAILRH) -->
            </Rail>`),
};

const RAILRH_32: PayloadSample = {
    json: j(baseShipment32('railrh-sample', [{
        transportMode: 'Rail',
        rail: {
            from: {
                postalCode: '10115',
                country: 'DE',
                // Optional address hints:
                // city: 'Berlin',
                // street: 'Station St 1',
            },
            to: {
                postalCode: '75001',
                country: 'FR',
                // Optional address hints:
                // city: 'Paris',
                // street: 'Station St 100',
            },
            type: 'RAILRH',
            truckType: 'R18D',
        },
    }])),
    xml: xmlWrap('3.2', 'railrh-sample', `
        <!-- Optional fields:
        <CustomerReference>CUST-REF-001</CustomerReference>
        <Reference>ORD-2024-001</Reference>
        <DateOfShipment>2024-06-15T08:00:00</DateOfShipment>
        <Commodity>General Cargo</Commodity>
        <Handling>false</Handling>
        <Volume>5.0</Volume>
        <VolumeUnit>M3</VolumeUnit>
        <Quote>false</Quote>
        <Cooling>false</Cooling>
        -->`, `            <Rail>
              <From><Address><PostalCode>10115</PostalCode><TwoLetterCountry>DE</TwoLetterCountry></Address></From>
              <To><Address><PostalCode>75001</PostalCode><TwoLetterCountry>FR</TwoLetterCountry></Address></To>
              <Type>RAILRH</Type>
              <TruckType>R18D</TruckType>
            </Rail>`),
};

const TRUCK_32: PayloadSample = {
    json: `{
  "version": "3.2",
  // "responseFormat": "standard",
  "shipments": [
    {
      "id": "truck-sample",
      "weight": 1000,
      // "customerReference": "CUST-REF-001",
      // "reference": "ORD-2024-001",
      // "dateOfShipment": "2024-06-15T08:00:00",
      // "commodity": "Electronics",
      // "handling": false,
      // "volume": 2.5,
      // "volumeUnit": "M3",
      // "weightUnit": "kg",
      // "quote": false,
      // "cooling": false,
      "legs": [
        {
          "transportMode": "Truck",
          "truck": {
            "from": {
              "postalCode": "3000",
              "twoLetterCountry": "CH",
              // "city": "Bern",
              // "street": "Main St 1",
              // "coordinates": {"lat": 46.9479, "long": 7.4474}
            },
            "to": {
              "postalCode": "8000",
              "twoLetterCountry": "CH",
              // "city": "Zurich",
              // "street": "Central St 100"
            },
            "type": "R18D",
            "loadFactor": 80,
            // "fuelType": "Diesel"
          }
        }
      ]
    }
  ]
}`,
    xml: xmlWrap('3.2', 'truck-sample', `
        <!-- Optional fields:
        <CustomerReference>CUST-REF-001</CustomerReference>
        <Reference>ORD-2024-001</Reference>
        <DateOfShipment>2024-06-15T08:00:00</DateOfShipment>
        <Commodity>Electronics</Commodity>
        <Handling>false</Handling>
        <Volume>2.5</Volume>
        <VolumeUnit>M3</VolumeUnit>
        <Quote>false</Quote>
        <Cooling>false</Cooling>
        -->`, `            <Truck>
              <From><Address><PostalCode>3000</PostalCode><TwoLetterCountry>CH</TwoLetterCountry></Address></From>
              <To><Address><PostalCode>8000</PostalCode><TwoLetterCountry>CH</TwoLetterCountry></Address></To>
              <Type>R18D</Type>
              <LoadFactor>80</LoadFactor>
              <!-- Optional: <FuelType>Diesel</FuelType> -->
            </Truck>`),
};

const IWW_32: PayloadSample = {
    json: `{
  "version": "3.2",
  // "responseFormat": "standard", // removed option marker
  "shipments": [
    {
      "id": "iww-sample",
      "weight": 1000,
      // "customerReference": "CUST-REF-001", // removed option marker
      // "reference": "ORD-2024-001", // removed option marker
      // "dateOfShipment": "2024-06-15T08:00:00", // removed option marker
      // "commodity": "Containerized Goods", // removed option marker
      // "handling": false, // removed option marker
      // "volume": 50.0, // removed option marker
      // "volumeUnit": "M3", // removed option marker
      // "quote": false, // removed option marker
      // "cooling": false, // removed option marker
      "legs": [
        {
          "transportMode": "Iww",
          "iww": {
            "from": {
              "postalCode": "68159",
              "twoLetterCountry": "DE",
              // "city": "Mannheim", // removed option marker
              // "street": "Port St 1"
            },
            "to": {
              "postalCode": "1011",
              "twoLetterCountry": "NL",
              // "city": "Amsterdam", // removed option marker
              // "street": "Port St 100"
            },
            "type": "IM135D"
          }
        }
      ]
    }
  ]
}`,
    xml: xmlWrap('3.2', 'iww-sample', `
        <!-- Optional fields:
        <CustomerReference>CUST-REF-001</CustomerReference>
        <Reference>ORD-2024-001</Reference>
        <DateOfShipment>2024-06-15T08:00:00</DateOfShipment>
        <Commodity>Containerized Goods</Commodity>
        <Handling>false</Handling>
        <Volume>50.0</Volume>
        <VolumeUnit>M3</VolumeUnit>
        <Quote>false</Quote>
        <Cooling>false</Cooling>
        -->`, `            <Iww>
              <From><Address><PostalCode>68159</PostalCode><TwoLetterCountry>DE</TwoLetterCountry></Address></From>
              <To><Address><PostalCode>1011</PostalCode><TwoLetterCountry>NL</TwoLetterCountry></Address></To>
              <Type>IM135D</Type>
            </Iww>`),
};

const FERRY_32: PayloadSample = {
    json: `{
  "version": "3.2",
  // "responseFormat": "standard",
  "shipments": [
    {
      "id": "ferry-sample",
      "weight": 1000,
      // "customerReference": "CUST-REF-001",
      // "reference": "ORD-2024-001",
      // "dateOfShipment": "2024-06-15T08:00:00",
      // "commodity": "General Cargo",
      // "handling": false,
      // "volume": 20.0,
      // "volumeUnit": "M3",
      // "quote": false,
      // "cooling": false,
      "legs": [
        {
          "transportMode": "Ferry",
          "ferry": {
            "from": {
              "postalCode": "HH",
              "twoLetterCountry": "DE",
              // "city": "Hamburg",
              // "street": "Port St 1"
            },
            "to": {
              "postalCode": "GOT",
              "twoLetterCountry": "SE",
              // "city": "Gothenburg",
              // "street": "Port St 100"
            },
            "type": "FROSD",
            // "truckType": "R18D"
          }
        }
      ]
    }
  ]
}`,
    xml: xmlWrap('3.2', 'ferry-sample', `
        <!-- Optional fields:
        <CustomerReference>CUST-REF-001</CustomerReference>
        <Reference>ORD-2024-001</Reference>
        <DateOfShipment>2024-06-15T08:00:00</DateOfShipment>
        <Commodity>General Cargo</Commodity>
        <Handling>false</Handling>
        <Volume>20.0</Volume>
        <VolumeUnit>M3</VolumeUnit>
        <Quote>false</Quote>
        <Cooling>false</Cooling>
        -->`, `            <Ferry>
              <From><Address><PostalCode>HH</PostalCode><TwoLetterCountry>DE</TwoLetterCountry></Address></From>
              <To><Address><PostalCode>GOT</PostalCode><TwoLetterCountry>SE</TwoLetterCountry></Address></To>
              <Type>FROSD</Type>
              <!-- Optional: <TruckType>R18D</TruckType> -->
            </Ferry>`),
};

const ROOT_32: PayloadSample = AIR_32;
const REQUEST_32: PayloadSample = AIR_32;
const SHIPMENT_32: PayloadSample = TRUCK_32;
const ADDR_32: PayloadSample = TRUCK_32;
const COORD_32: PayloadSample = {
    json: j(baseShipment32('coord-sample', [{
        transportMode: 'Truck',
        truck: {
            from: {
                coordinates: {
                    lat: 47.3769,
                    long: 8.5417,
                    // Optional hints:
                    // postalCode: '3000',
                    // twoLetterCountry: 'CH',
                    // city: 'Bern',
                    // street: 'Main St 1',
                }
            },
            to: {
                postalCode: '3000',
                country: 'CH',
                // Optional address hints:
                // city: 'Bern',
                // street: 'Station St 1',
            },
           
        },
    }])),
    xml: xmlWrap('3.2', 'coord-sample', '', `            <Truck>
              <From><Coordinates><Lat>47.3769</Lat><Long>8.5417</Long></Coordinates></From>
              <To><Address><PostalCode>3000</PostalCode><TwoLetterCountry>CH</TwoLetterCountry></Address></To>
            </Truck>`),
};
const AIR_ADDR_32: PayloadSample = AIR_32;
const SEA_ADDR_32: PayloadSample = SEA_32;

// ─── v3.0 Samples ─────────────────────────────────────────────────────────────

const AIR_30: PayloadSample = {
    json: j(baseShipment30('air-30-sample', [{
        transportMode: 'Air',
        air: {
            from: {
                airportCode: 'FRA',
               
                // coordinates: { lat: 50.0379, long: 8.5622 }
            },
            to: {
                airportCode: 'JFK',
               
                // coordinates: { lat: 40.6413, long: -73.7781 }
            },
            isFreighter: false,
            acType: 'B744', // Optional aircraft type code
           
        },
    }])),
    xml: xmlWrap30('air-30-sample', '', `            <Air>
              <From><AirPortCode>FRA</AirPortCode></From>
              <To><AirPortCode>JFK</AirPortCode></To>
              <IsFreighter>false</IsFreighter>
              <ACType>B744</ACType>
            </Air>`),
};

const SEA_30: PayloadSample = {
    json: j(baseShipment30('sea-30-sample', [{
        transportMode: 'Sea',
        sea: {
            from: {
                seaportCode: 'DEHAM',
               
                // coordinates: { lat: 53.5400, long: 9.9900 }
            },
            to: {
                seaportCode: 'NLRTM',
               
                // coordinates: { lat: 51.9225, long: 4.2281 }
            },
           
        },
    }])),
    xml: xmlWrap30('sea-30-sample', '', `            <Sea>
              <From><SeaPortCode>DEHAM</SeaPortCode></From>
              <To><SeaPortCode>NLRTM</SeaPortCode></To>
            </Sea>`),
};

const RAIL_30: PayloadSample = {
    json: j(baseShipment30('rail-30-sample', [{
        transportMode: 'Rail',
        rail: {
            from: {
                postalCode: '10115',
                country: 'DE',
                // Optional address hints:
                // city: 'Berlin',
                // street: 'Station St 1',
            },
            to: {
                postalCode: '75001',
                country: 'FR',
                // Optional address hints:
                // city: 'Paris',
                // street: 'Station St 100',
            }
            // Note: Rail Highway (RAILRH) is not available in v3.0, added in v3.2
        },
    }])),
    xml: xmlWrap30('rail-30-sample', '', `            <Rail>
              <From><Address><PostalCode>10115</PostalCode><TwoLetterCountry>DE</TwoLetterCountry></Address></From>
              <To><Address><PostalCode>75001</PostalCode><TwoLetterCountry>FR</TwoLetterCountry></Address></To>
            </Rail>`),
};

const TRUCK_30: PayloadSample = {
    json: j(baseShipment30('truck-30-sample', [{
        transportMode: 'Truck',
        truck: {
            from: {
                postalCode: '3000',
                country: 'CH',
                // Optional address hints:
                // city: 'Bern',
                // street: 'Main St 1',
            },
            to: {
                postalCode: '8000',
                country: 'CH',
                // Optional address hints:
                // city: 'Zurich',
                // street: 'Central St 100',
            },
            type: '13-18T', // Descriptive labels: automatic, 3.5T, 7T, 13-18T, 22-36T, 40T
           
            // loadFactor: 80, // Any integer 0-100 (not restricted in v3.0, unlike v3.2)
        },
    }])),
    xml: xmlWrap30('truck-30-sample', '', `            <Truck>
              <From><Address><PostalCode>3000</PostalCode><TwoLetterCountry>CH</TwoLetterCountry></Address></From>
              <To><Address><PostalCode>8000</PostalCode><TwoLetterCountry>CH</TwoLetterCountry></Address></To>
              <Type>13-18T</Type>
              <LoadFactor>80</LoadFactor>
            </Truck>`),
};

const IWW_30: PayloadSample = {
    json: j(baseShipment30('iww-30-sample', [{
        transportMode: 'Iww',
        iww: {
            from: {
                postalCode: '68159',
                country: 'DE',
                // Optional address hints:
                // city: 'Mannheim',
                // street: 'Port St 1',
            },
            to: {
                postalCode: '1011',
                country: 'NL',
                // Optional address hints:
                // city: 'Amsterdam',
                // street: 'Port St 100',
            },
            type: 4, // Numeric integer 0-11 (mapped to vessel types). v3.2 uses named codes instead.
        },
    }])),
    xml: xmlWrap30('iww-30-sample', '', `            <Iww>
              <From><Address><PostalCode>68159</PostalCode><TwoLetterCountry>DE</TwoLetterCountry></Address></From>
              <To><Address><PostalCode>1011</PostalCode><TwoLetterCountry>NL</TwoLetterCountry></Address></To>
              <Type>4</Type>
            </Iww>`),
};

const FERRY_30: PayloadSample = {
    json: j(baseShipment30('ferry-30-sample', [{
        transportMode: 'Ferry',
        ferry: {
            from: {
                postalCode: 'HH',
                country: 'DE',
                // Optional address hints:
                // city: 'Hamburg',
                // street: 'Port St 1',
            },
            to: {
                postalCode: 'GOT',
                country: 'SE',
                // Optional address hints:
                // city: 'Gothenburg',
                // street: 'Port St 100',
            },
            type: 'roro_large', // Types: roro_large, roro_small, ropax_large, ropax_small (v3.0 format; v3.2 uses FR codes)
           
            // truckType: '13-18T', // Optional. Descriptive labels: automatic, 3.5T, 7T, 13-18T, 22-36T, 40T
        },
    }])),
    xml: xmlWrap30('ferry-30-sample', '', `            <Ferry>
              <From><Address><PostalCode>HH</PostalCode><TwoLetterCountry>DE</TwoLetterCountry></Address></From>
              <To><Address><PostalCode>GOT</PostalCode><TwoLetterCountry>SE</TwoLetterCountry></Address></To>
              <Type>roro_large</Type>
              <TruckType>13-18T</TruckType>
            </Ferry>`),
};

// ─── v2.8 Samples ─────────────────────────────────────────────────────────────

const xmlWrap28 = (id: string, note: string, leg: string) => xmlWrap('2.8', id, note, leg);

const AIR_28: PayloadSample = {
    json: j(baseShipment28('air-28-sample', [{
        transportMode: 'Air',
        air: {
            from: {
                airportCode: 'FRA',
               
                // coordinates: { lat: 50.0379, long: 8.5622 }
            },
            to: {
                airportCode: 'JFK',
               
                // coordinates: { lat: 40.6413, long: -73.7781 }
            },
            isFreighter: false,
           
            // acType: 'B744', // Optional aircraft type code (v2.8 only; v3.2 uses airType instead)
        },
    }])),
    xml: xmlWrap28('air-28-sample', '', `            <Air>
              <From><AirPortCode>FRA</AirPortCode></From>
              <To><AirPortCode>JFK</AirPortCode></To>
              <IsFreighter>false</IsFreighter>
              <ACType>B744</ACType>
            </Air>`),
};

const SEA_28: PayloadSample = {
    json: j(baseShipment28('sea-28-sample', [{
        transportMode: 'Sea',
        sea: {
            from: {
                seaportCode: 'DEHAM',
               
                // coordinates: { lat: 53.5400, long: 9.9900 }
            },
            to: {
                seaportCode: 'NLRTM',
               
                // coordinates: { lat: 51.9225, long: 4.2281 }
            }
        },
    }])),
    xml: xmlWrap28('sea-28-sample', '', `            <Sea>
              <From><SeaPortCode>DEHAM</SeaPortCode></From>
              <To><SeaPortCode>NLRTM</SeaPortCode></To>
            </Sea>`),
};

const RAIL_28: PayloadSample = {
    json: j(baseShipment28('rail-28-sample', [{
        transportMode: 'Rail',
        rail: {
            from: {
                postalCode: '10115',
                country: 'DE',
                // Optional address hints:
                // city: 'Berlin',
                // street: 'Station St 1',
            },
            to: {
                postalCode: '75001',
                country: 'FR',
                // Optional address hints:
                // city: 'Paris',
                // street: 'Station St 100',
            }
            // Note: Rail Highway (RAILRH) not available in v2.8 (added in v3.2)
        },
    }])),
    xml: xmlWrap28('rail-28-sample', '', `            <Rail>
              <From><Address><PostalCode>10115</PostalCode><TwoLetterCountry>DE</TwoLetterCountry></Address></From>
              <To><Address><PostalCode>75001</PostalCode><TwoLetterCountry>FR</TwoLetterCountry></Address></To>
            </Rail>`),
};

const TRUCK_28: PayloadSample = {
    json: j(baseShipment28('truck-28-sample', [{
        transportMode: 'Truck',
        truck: {
            from: {
                postalCode: '3000',
                country: 'CH',
                // Optional address hints:
                // city: 'Bern',
                // street: 'Main St 1',
            },
            to: {
                postalCode: '8000',
                country: 'CH',
                // Optional address hints:
                // city: 'Zurich',
                // street: 'Central St 100',
            },
            type: '13-18T', // Descriptive labels: automatic, 3.5T, 7T, 13-18T, 22-36T, 40T
           
            // loadFactor: 80, // Any integer 0-100
            // calculationMode: 'RoadDistance', // GreatCircleDistance or RoadDistance (v2.8 only; removed in v3.0+)
        },
    }])),
    xml: xmlWrap28('truck-28-sample', '', `            <Truck>
              <From><Address><PostalCode>3000</PostalCode><TwoLetterCountry>CH</TwoLetterCountry></Address></From>
              <To><Address><PostalCode>8000</PostalCode><TwoLetterCountry>CH</TwoLetterCountry></Address></To>
              <Type>13-18T</Type>
              <LoadFactor>80</LoadFactor>
              <CalculationMode>RoadDistance</CalculationMode>
            </Truck>`),
};

const IWW_28: PayloadSample = {
    json: j(baseShipment28('iww-28-sample', [{
        transportMode: 'Iww',
        iww: {
            from: {
                postalCode: '68159',
                country: 'DE',
                // Optional address hints:
                // city: 'Mannheim',
                // street: 'Port St 1',
            },
            to: {
                postalCode: '1011',
                country: 'NL',
                // Optional address hints:
                // city: 'Amsterdam',
                // street: 'Port St 100',
            },
            type: 4, // Numeric integer 0-13 (v2.8 supports wider range than v3.0's 0-11). v3.2 uses named codes.
        },
    }])),
    xml: xmlWrap28('iww-28-sample', '', `            <Iww>
              <From><Address><PostalCode>68159</PostalCode><TwoLetterCountry>DE</TwoLetterCountry></Address></From>
              <To><Address><PostalCode>1011</PostalCode><TwoLetterCountry>NL</TwoLetterCountry></Address></To>
              <Type>4</Type>
            </Iww>`),
};

const FERRY_28: PayloadSample = {
    json: j(baseShipment28('ferry-28-sample', [{
        transportMode: 'Ferry',
        ferry: {
            from: {
                postalCode: 'HH',
                country: 'DE',
                // Optional address hints:
                // city: 'Hamburg',
                // street: 'Port St 1',
            },
            to: {
                postalCode: 'GOT',
                country: 'SE',
                // Optional address hints:
                // city: 'Gothenburg',
                // street: 'Port St 100',
            },
            type: 'large' // Types: large, small (v2.8 only; v3.0 uses roro_*/ropax_*, v3.2 uses FR codes)
            // Note: TruckType not available in v2.8 (added in v3.0)
        },
    }])),
    xml: xmlWrap28('ferry-28-sample', '', `            <Ferry>
              <From><Address><PostalCode>HH</PostalCode><TwoLetterCountry>DE</TwoLetterCountry></Address></From>
              <To><Address><PostalCode>GOT</PostalCode><TwoLetterCountry>SE</TwoLetterCountry></Address></To>
              <Type>large</Type>
            </Ferry>`),
};

// ─── Registry: version → sectionId → sample ──────────────────────────────────

export const SECTION_SAMPLES: Record<string, Record<string, PayloadSample>> = {
    '3.2': {
        root: ROOT_32, request: REQUEST_32, shipment: SHIPMENT_32,
        air: AIR_32, sea: SEA_32, rail: RAIL_32, truck: TRUCK_32,
        iww: IWW_32, ferry: FERRY_32,
        response: AIR_32,
        address: ADDR_32, coordinates: COORD_32,
        airAddress: AIR_ADDR_32, seaAddress: SEA_ADDR_32,
        // bonus: rail highway variant
        rail_rh: RAILRH_32,
    },
    '3.0': {
        root: AIR_30, request: AIR_30, shipment: TRUCK_30,
        air: AIR_30, sea: SEA_30, rail: RAIL_30, truck: TRUCK_30,
        iww: IWW_30, ferry: FERRY_30,
        response: AIR_30,
        address: TRUCK_30, coordinates: TRUCK_30,
        airAddress: AIR_30, seaAddress: SEA_30,
    },
    '2.8': {
        root: AIR_28, request: AIR_28, shipment: TRUCK_28,
        air: AIR_28, sea: SEA_28, rail: RAIL_28, truck: TRUCK_28,
        iww: IWW_28, ferry: FERRY_28,
        response: TRUCK_28,
        address: TRUCK_28, coordinates: TRUCK_28,
        airAddress: AIR_28, seaAddress: SEA_28,
    },
};
